import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { LinkedinMoment, LinkedinTemplate } from "../../interfaces/linkedinProspecting";
import { createEmailTemplate, deleteEmailTemplate, updateEmailTemplate } from "../../interfaces/emailTemplate";
import { updateMyInfo } from "../../interfaces/user";
import { Button } from "../../components/ui/Button";
import { SectionTitle } from "./parts";
import { DEFAULT_OFFER, INVITE_NOTE_LIMIT, MESSAGE_MOMENTS, OFFER_TOKEN, momentOf } from "./vocabulary";
import { exampleValues } from "./variables";
import { VariableTextarea } from "./VariableTextarea";

const MOMENT_LABEL: Record<LinkedinMoment, string> = {
    invitation: "Invitation",
    ...Object.fromEntries(MESSAGE_MOMENTS.map((m) => [m.key, m.label])),
} as Record<LinkedinMoment, string>;

/** Same cap as the backend: the offer sits inside a 300-character invitation note. */
const OFFER_MAX = 200;

const input =
    "w-full rounded-lg border-0 bg-surface-sunken px-3 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/**
 * Everything the LinkedIn messages are made of, editable without leaving the LinkedIn page: the
 * rep's offer (the {votre offre} variable, also in Mon compte) and their LinkedIn templates —
 * plain templates of type "linkedin", the same ones the templates modal lists — in two groups:
 * the invitation notes, and the messages once connected (each with its moment).
 */
export default function MessagesManager({
    offer, templates, focus, onOfferSaved, onTemplatesChange, onClose,
}: {
    offer: string;
    templates: LinkedinTemplate[];
    /** Which part to land on. */
    focus: "offer" | "templates";
    onOfferSaved: (offer: string) => void;
    onTemplatesChange: (templates: LinkedinTemplate[]) => void;
    onClose: () => void;
}) {
    const [draftOffer, setDraftOffer] = useState(offer);
    const [savingOffer, setSavingOffer] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const offerRef = useRef<HTMLTextAreaElement>(null);
    const templatesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (focus === "offer") offerRef.current?.focus();
        else templatesRef.current?.scrollIntoView({ block: "start" });
    }, [focus]);

    useEffect(() => {
        const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [onClose]);

    const saveOffer = async () => {
        setSavingOffer(true);
        const res: any = await updateMyInfo({ prospectingOffer: draftOffer.trim() });
        setSavingOffer(false);
        if (res?.data) {
            onOfferSaved(draftOffer.trim());
            toast.success("Offre enregistrée.");
        } else toast.error("Impossible d'enregistrer l'offre.");
    };

    const addTemplate = async (moment: LinkedinMoment) => {
        try {
            const created = await createEmailTemplate({
                name: moment === "invitation" ? "Nouvelle invitation" : "Nouveau message",
                text: `Bonjour {firstName}, ${OFFER_TOKEN} Ravi d'échanger si le sujet vous parle.`,
                type: "linkedin",
                lang: "fr",
                ...(moment === "invitation" ? {} : { linkedinMoment: moment }),
            });
            onTemplatesChange([...templates, created]);
            setEditingId(created._id);
        } catch {
            toast.error("Impossible de créer le template.");
        }
    };

    // z-[60]: also opened from the invite and message dialogs, and must sit above them.
    return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/40 sm:items-center sm:p-4" onMouseDown={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Messages LinkedIn"
                onMouseDown={(e) => e.stopPropagation()}
                className="flex max-h-screen w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-surface shadow-float sm:max-h-[90vh] sm:rounded-2xl"
            >
                <div className="flex items-start gap-3 px-5 pb-3 pt-5">
                    <div className="min-w-0 flex-1">
                        <h2 className="font-sans text-lg font-semibold text-ink">Messages LinkedIn</h2>
                        <p className="font-sans text-sm text-ink-muted">Votre offre et vos templates : l'invitation, puis les messages une fois connecté.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-ink-faint hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        aria-label="Fermer"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {/* Offer */}
                    <div className="border-t border-border px-5 py-4">
                        <SectionTitle>Votre offre</SectionTitle>
                        <p className="mb-2 font-sans text-xs text-ink-muted">
                            Une phrase sur ce que vous proposez. Elle remplace{" "}
                            <span className="rounded-full bg-accent-subtle px-1.5 py-0.5 font-mono text-accent">{OFFER_TOKEN}</span> dans vos messages.
                        </p>
                        <textarea
                            ref={offerRef}
                            value={draftOffer}
                            onChange={(e) => setDraftOffer(e.target.value.slice(0, OFFER_MAX))}
                            rows={2}
                            placeholder={DEFAULT_OFFER}
                            className={`${input} resize-none py-2`}
                        />
                        <div className="mt-2 flex items-center gap-3">
                            <span className="font-mono text-xs text-ink-faint">
                                {draftOffer.length} / {OFFER_MAX}
                            </span>
                            {!draftOffer.trim() && <span className="font-sans text-xs text-ink-faint">Vide : la phrase grisée est utilisée.</span>}
                            <Button size="sm" className="ml-auto" disabled={savingOffer || draftOffer.trim() === offer.trim()} onClick={saveOffer}>
                                {savingOffer && <Loader2 className="h-4 w-4 animate-spin" />}
                                Enregistrer
                            </Button>
                        </div>
                    </div>

                    {/* Templates, in two groups: the invitation notes, then the messages once connected */}
                    <div ref={templatesRef}>
                        {[
                            { title: "Invitation", hint: `La note jointe à l'invitation, ${INVITE_NOTE_LIMIT} caractères au plus.`, invitation: true },
                            { title: "Après l'invitation", hint: "Les messages une fois connecté, proposés selon l'étape du prospect.", invitation: false },
                        ].map((group) => {
                            const list = templates.filter((t) => (momentOf(t) === "invitation") === group.invitation);
                            if (!group.invitation) list.sort((a, b) => MESSAGE_MOMENTS.findIndex((m) => m.key === momentOf(a)) - MESSAGE_MOMENTS.findIndex((m) => m.key === momentOf(b)));
                            return (
                                <div key={group.title} className="border-t border-border px-5 py-4">
                                    <div className="mb-1 flex items-center justify-between">
                                        <SectionTitle>{group.title}</SectionTitle>
                                        <button
                                            onClick={() => addTemplate(group.invitation ? "invitation" : "first_message")}
                                            className="-mt-2 inline-flex items-center gap-1 rounded font-sans text-xs font-medium text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        >
                                            <Plus className="h-3.5 w-3.5" />
                                            Nouveau template
                                        </button>
                                    </div>
                                    <p className="mb-2 font-sans text-xs text-ink-muted">{group.hint}</p>
                                    {list.length === 0 ? (
                                        <p className="font-sans text-sm text-ink-faint">Aucun template.</p>
                                    ) : (
                                        <ul className="divide-y divide-border rounded-2xl border border-border">
                                            {list.map((t) =>
                                                editingId === t._id ? (
                                                    <TemplateEditor
                                                        key={t._id}
                                                        template={t}
                                                        offer={offer}
                                                        onCancel={() => setEditingId(null)}
                                                        onSaved={(saved) => {
                                                            onTemplatesChange(templates.map((x) => (x._id === saved._id ? saved : x)));
                                                            setEditingId(null);
                                                        }}
                                                        onDeleted={() => {
                                                            onTemplatesChange(templates.filter((x) => x._id !== t._id));
                                                            setEditingId(null);
                                                        }}
                                                    />
                                                ) : (
                                                    <li key={t._id}>
                                                        <button
                                                            onClick={() => setEditingId(t._id)}
                                                            className="block w-full px-4 py-3 text-left hover:bg-surface-sunken/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                                                        >
                                                            <p className="flex items-center gap-2 font-sans text-sm font-medium text-ink">
                                                                {t.name}
                                                                {!group.invitation && (
                                                                    <span className="rounded-full bg-surface-sunken px-2 py-0.5 font-sans text-xs font-normal text-ink-muted">
                                                                        {MOMENT_LABEL[momentOf(t)]}
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="line-clamp-2 font-sans text-xs text-ink-muted">{t.text}</p>
                                                        </button>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function TemplateEditor({
    template, offer, onCancel, onSaved, onDeleted,
}: {
    template: LinkedinTemplate;
    /** For the {votre offre} example. */
    offer: string;
    onCancel: () => void;
    onSaved: (t: LinkedinTemplate) => void;
    onDeleted: () => void;
}) {
    const [name, setName] = useState(template.name);
    const [text, setText] = useState(template.text);
    const [moment, setMoment] = useState<LinkedinMoment>(momentOf(template));
    const [busy, setBusy] = useState(false);
    const invitation = momentOf(template) === "invitation";
    const examples = useMemo(() => exampleValues(offer), [offer]);

    const save = async () => {
        setBusy(true);
        try {
            const saved = await updateEmailTemplate(template._id, {
                name: name.trim() || template.name,
                text,
                ...(invitation ? {} : { linkedinMoment: moment }),
            });
            onSaved(saved);
        } catch {
            toast.error("Impossible d'enregistrer le template.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer le template « ${template.name} » ?`)) return;
        setBusy(true);
        try {
            await deleteEmailTemplate(template._id);
            onDeleted();
        } catch {
            toast.error("Impossible de supprimer le template.");
            setBusy(false);
        }
    };

    return (
        <li className="space-y-2 bg-surface-sunken/40 px-4 py-3">
            <div className="flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} className={`${input} h-9 flex-1 bg-surface font-medium`} aria-label="Nom du template" />
                {!invitation && (
                    <select
                        value={moment}
                        onChange={(e) => setMoment(e.target.value as LinkedinMoment)}
                        aria-label="Moment"
                        className={`${input} h-9 w-auto bg-surface`}
                    >
                        {MESSAGE_MOMENTS.map((m) => (
                            <option key={m.key} value={m.key}>
                                {m.label}
                            </option>
                        ))}
                    </select>
                )}
            </div>
            <VariableTextarea value={text} onChange={setText} values={examples} examples minRows={4} />
            <div className="flex items-center gap-2 pt-1">
                <button
                    onClick={remove}
                    disabled={busy}
                    className="inline-flex items-center gap-1 rounded font-sans text-xs text-ink-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                    Supprimer
                </button>
                {invitation && <span className="font-sans text-xs text-ink-faint">Une invitation fait {INVITE_NOTE_LIMIT} caractères au plus, variables remplies.</span>}
                <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
                        Annuler
                    </Button>
                    <Button size="sm" onClick={save} disabled={busy || !text.trim()}>
                        Enregistrer
                    </Button>
                </div>
            </div>
        </li>
    );
}
